/**
 * Generic asset resolution with a bundled-first, server-fallback strategy.
 *
 * Any game asset (voice MP3, SFX opus, and — in future — images, etc.) is
 * addressed by a relative path like `voice/scan.mp3` or `sfx/fire.opus`.
 *
 *   1. Try the BUNDLED copy at the app's own origin (`appOrigin/<path>`). These
 *      ship with the client, so they load instantly and offline.
 *   2. On miss (404 / network error), fall back to the configured backend
 *      (`serverAssetBase/<path>`). This lets the server introduce new assets
 *      (e.g. voice lines for new game mechanics) without a client release.
 *
 * We deliberately keep no manual cache layer — the browser/WebView HTTP cache
 * persists everything. Per-path fetch promises are memoised for the session so
 * concurrent requests share one network round-trip.
 */
import { appOrigin, config } from './config';

const inflight = new Map<string, Promise<ArrayBuffer | null>>();

function join(base: string, path: string): string {
  return `${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
}

async function tryFetch(url: string): Promise<ArrayBuffer | null> {
  try {
    const res = await fetch(url, { cache: 'force-cache' });
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
}

/**
 * Fetch an asset's bytes, bundled-first then server-fallback.
 * Returns null if neither source has it.
 */
export function loadAsset(path: string): Promise<ArrayBuffer | null> {
  let p = inflight.get(path);
  if (!p) {
    p = (async () => {
      const local = await tryFetch(join(appOrigin, path));
      if (local) return local;
      // Only bother with the fallback if the server is a different origin.
      const serverUrl = join(config.serverAssetBase, path);
      if (serverUrl !== join(appOrigin, path)) {
        const remote = await tryFetch(serverUrl);
        if (remote) return remote;
      }
      return null;
    })();
    inflight.set(path, p);
  }
  return p;
}

/** Forget a memoised asset (rarely needed; the HTTP cache still applies). */
export function forgetAsset(path: string): void {
  inflight.delete(path);
}

/** Fetch + parse a JSON asset (bundled-first, server-fallback). */
export async function loadJsonAsset<T>(path: string): Promise<T | null> {
  const buf = await loadAsset(path);
  if (!buf) return null;
  try {
    return JSON.parse(new TextDecoder().decode(buf)) as T;
  } catch {
    return null;
  }
}
